import { computed, ref, watch } from "vue";
import { createGlobalState } from "@vueuse/core";
import { useReadonlyWithSetter, usePromiseWrapper } from "@/composables/common";
import { ApplicationsHttpService } from "@/api/http/ApplicationsHttpService";
import useApplicationsState from "@/composables/state/applicationsState";
import { ApplicationPrivateChannel } from "@/api/ws/channels/ApplicationPrivateChannel";
import CheckoutProgressWasUpdatedEvent from "@/api/ws/events/CheckoutProgressWasUpdatedEvent";
import CheckoutSnapshotWasUpdatedEvent from "@/api/ws/events/CheckoutSnapshotWasUpdatedEvent";

import type {
  CheckoutTemplate,
  CheckoutProgress,
  CheckoutStep,
} from "@/api/http/schemas/checkoutSchemas";

const useCheckoutState = createGlobalState(() => {
  const { selectedApplication, acceptedOffers, blocksAreActive } = useApplicationsState();

  const [checkout, setCheckout] = useReadonlyWithSetter<CheckoutTemplate | null>(null);
  const [selectedStepId, setSelectedStepId] = useReadonlyWithSetter<string | null>(null);

  const applicationChannel = ref<ApplicationPrivateChannel | null>(null);
  const checkoutProgress = ref<CheckoutProgress | null>(null);

  const { executeFetch: getCheckout } = usePromiseWrapper(async () => {
    const { id: applicationId, is_checkout_enabled } = selectedApplication.value ?? {};

    if (!applicationId || !is_checkout_enabled) {
      return;
    }

    const applicationsService = new ApplicationsHttpService();

    const checkout = await applicationsService.getCheckout(applicationId);

    setCheckout(checkout);
    return checkout;
  });

  const steps = computed(() => checkout.value?.content?.steps ?? []);

  const selectedStep = computed(() => steps.value.find((step) => step.id === selectedStepId.value));

  const firstStepIsSelected = computed(() => {
    const firstStep = steps.value[0];
    return selectedStep.value?.id === firstStep?.id;
  });

  const lastStepIsSelected = computed(() => {
    const lastStep = steps.value[steps.value.length - 1];
    return selectedStep.value?.id === lastStep.id;
  });

  const isActive = computed(
    () =>
      !!selectedApplication.value &&
      selectedApplication.value.is_checkout_enabled &&
      acceptedOffers.value.some((offer) => offer.has_checkout_snapshot) &&
      blocksAreActive(["checkout"])
  );

  const stepIsComplete = (stepId: CheckoutStep["id"]) => {
    return checkoutProgress.value?.step_statuses.some(
      (step) => step.id === stepId && step.is_complete
    );
  };

  const refreshProgress = async () => {
    const { id: applicationId, is_checkout_enabled } = selectedApplication.value || {};

    if (!applicationId || !is_checkout_enabled) {
      return;
    }

    const applicationsService = new ApplicationsHttpService();

    checkoutProgress.value = await applicationsService.getCheckoutProgress(applicationId);
    return checkoutProgress;
  };

  const navigateSteps = (direction: "next" | "previous") => {
    const currentStepIndex = steps.value.findIndex((step) => step.id === selectedStepId.value);

    if (currentStepIndex === -1) {
      return;
    }

    const stepToNavigateTo =
      direction === "next" ? steps.value[currentStepIndex + 1] : steps.value[currentStepIndex - 1];

    if (!stepToNavigateTo) {
      return;
    }

    setSelectedStepId(stepToNavigateTo.id);
  };

  watch(
    selectedApplication,
    (application) => {
      if (!application?.is_checkout_enabled) {
        return;
      }

      const currentStage = selectedApplication.value?.stages.find((stage) => stage.is_current);

      if (!currentStage?.blocks?.includes("checkout")) {
        return;
      }

      getCheckout();
    },
    { immediate: true }
  );

  watch(
    checkout,
    async (newCheckout, oldCheckout) => {
      if (!newCheckout || newCheckout?.id === oldCheckout?.id) {
        return;
      }

      await refreshProgress();

      const firstStep = newCheckout?.content.steps[0]?.id ?? null;
      setSelectedStepId(firstStep);
    },
    { immediate: true }
  );

  watch(
    () => selectedApplication.value?.id,
    (newDealId, oldDealId) => {
      if (!newDealId || !!oldDealId) {
        applicationChannel.value?.leave();
      }

      if (!newDealId) {
        return;
      }

      applicationChannel.value = new ApplicationPrivateChannel(newDealId);

      applicationChannel.value?.subscribeTo(
        new CheckoutSnapshotWasUpdatedEvent((event) => {
          if (!checkout.value) {
            return;
          }

          checkout.value.content = event.content;
        }),
        new CheckoutProgressWasUpdatedEvent((event) => {
          if (!checkoutProgress.value) {
            return;
          }

          checkoutProgress.value.status = event.status;
          checkoutProgress.value.step_statuses = event.step_statuses;
        })
      );
    },
    { immediate: true }
  );

  return {
    checkout,
    checkoutProgress,
    firstStepIsSelected,
    isActive,
    lastStepIsSelected,
    selectedStepId,
    steps,
    getCheckout,
    navigateSteps,
    refreshProgress,
    setCheckout,
    setSelectedStepId,
    stepIsComplete,
  };
});

export { useCheckoutState };
